import {
  ColumnState,
  GridState,
  SortModelItem,
  AggregationColumnState,
  ColumnSizeState
} from '@ag-grid-community/core';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { stateKey } from '../components/tables/table-state-persistence';

const oldColStateKey = 'colState';
const oldFilterStateKey = 'filterState';

// This function converts the old URL state format to the new one used by ag-Grid.
// Can be removed after a transition period when all users have migrated to the new format.
export function convertOldUrlState() {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const oldFilters = searchParams.get(oldFilterStateKey);
    const oldColState = searchParams.get(oldColStateKey);

    const filters = oldFilters
      ? JSON.parse(decompressFromEncodedURIComponent(oldFilters))
      : undefined;
    const colState = oldColState
      ? JSON.parse(decompressFromEncodedURIComponent(oldColState))
      : undefined;

    if (colState || filters) {
      const state = {
        ...(colState ? convertColumnState(colState as ColumnState[]) : {}),
        filter: filters ? { filterModel: filters } : undefined
      };
      searchParams.delete(oldFilterStateKey);
      searchParams.delete(oldColStateKey);
      searchParams.set('state', compressToEncodedURIComponent(JSON.stringify(stateKey)));
      const base = location.pathname + location.hash;
      const query = searchParams.toString();
      const newUrl = query ? `${base}?${query}` : base;
      window.history.replaceState(null, '', newUrl);
      return state;
    }

    return undefined;
  } catch (e) {
    console.error('Failed to convert old URL state', e);
    return undefined;
  }
}

export function convertColumnState(
  columnState: ColumnState[]
): Pick<
  GridState,
  | 'sort'
  | 'rowGroup'
  | 'aggregation'
  | 'pivot'
  | 'columnPinning'
  | 'columnVisibility'
  | 'columnSizing'
  | 'columnOrder'
> {
  const sortColumns: SortModelItem[] = [];
  const groupColIds: string[] = [];
  const aggregationColumns: AggregationColumnState[] = [];
  const pivotColIds: string[] = [];
  const leftColIds: string[] = [];
  const rightColIds: string[] = [];
  const hiddenColIds: string[] = [];
  const columnSizes: ColumnSizeState[] = [];
  const columns: string[] = [];

  let defaultSortIndex = 0;
  for (let i = 0; i < columnState.length; i++) {
    const {
      colId,
      sort,
      sortIndex,
      rowGroup,
      rowGroupIndex,
      aggFunc,
      pivot,
      pivotIndex,
      pinned,
      hide,
      width,
      flex
    } = columnState[i];
    columns.push(colId);
    if (sort) {
      sortColumns[sortIndex ?? defaultSortIndex++] = { colId, sort };
    }
    if (rowGroup) {
      groupColIds[rowGroupIndex ?? 0] = colId;
    }
    if (typeof aggFunc === 'string') {
      aggregationColumns.push({ colId, aggFunc });
    }
    if (pivot) {
      pivotColIds[pivotIndex ?? 0] = colId;
    }
    if (pinned) {
      (pinned === 'right' ? rightColIds : leftColIds).push(colId);
    }
    if (hide) {
      hiddenColIds.push(colId);
    }
    if (flex != null || width) {
      columnSizes.push({ colId, flex: flex ?? undefined, width });
    }
  }

  return {
    sort: sortColumns.length ? { sortModel: sortColumns } : undefined,
    rowGroup: groupColIds.length ? { groupColIds } : undefined,
    aggregation: aggregationColumns.length ? { aggregationModel: aggregationColumns } : undefined,
    pivot: pivotColIds.length ? { pivotMode: false, pivotColIds } : undefined,
    columnPinning:
      leftColIds.length || rightColIds.length ? { leftColIds, rightColIds } : undefined,
    columnVisibility: hiddenColIds.length ? { hiddenColIds } : undefined,
    columnSizing: columnSizes.length ? { columnSizingModel: columnSizes } : undefined,
    columnOrder: columns.length ? { orderedColIds: columns } : undefined
  };
}
